import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdminArea,
  AdminNvrDevice,
  AdminVideoSource,
  ApiError,
  PagedResult,
  VideoCatalogSyncLog,
  VideoCatalogSyncResult,
  api,
} from '../lib/api';
import { useI18n } from '../i18n';

type Tab = 'sources' | 'nvrs' | 'sync';

type SourceEditor = {
  id: number;
  displayName: string;
  areaId: string;
  isEnabled: boolean;
  sortOrder: number;
  notes: string;
  updatedAt: string;
};

type AreaEditor = {
  id?: number;
  name: string;
  sortOrder: number;
  enabled: boolean;
  updatedAt?: string;
};

type NvrEditor = {
  id: number;
  deviceId: string;
  displayName: string;
  updatedAt: string;
};

const emptyResult: PagedResult<AdminVideoSource> = {
  items: [],
  page: 1,
  pageSize: 50,
  totalCount: 0,
};

function readableError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return `${error.message}${error.traceId ? `（Trace ${error.traceId}）` : ''}`;
  }
  return error instanceof Error ? error.message : fallback;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function onlineLabel(status: string, t: (key: string) => string): string {
  return status === 'ONLINE'
    ? t('common.online')
    : status === 'OFFLINE'
      ? t('common.offline')
      : t('common.unknown');
}

function discoveryLabel(status: string, t: (key: string) => string): string {
  switch (status) {
    case 'IMPORTED': return t('streams.imported');
    case 'DISCOVERED': return t('streams.discovered');
    case 'IGNORED': return t('streams.ignored');
    default: return status;
  }
}

export function VideoStreamsPage() {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('sources');
  const [areas, setAreas] = useState<AdminArea[]>([]);
  const [sources, setSources] = useState<PagedResult<AdminVideoSource>>(emptyResult);
  const [nvrs, setNvrs] = useState<AdminNvrDevice[]>([]);
  const [logs, setLogs] = useState<VideoCatalogSyncLog[]>([]);
  const [search, setSearch] = useState('');
  const [areaId, setAreaId] = useState('');
  const [onlineStatus, setOnlineStatus] = useState('');
  const [enabled, setEnabled] = useState('');
  const [discoveryStatus, setDiscoveryStatus] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sourceEditor, setSourceEditor] = useState<SourceEditor>();
  const [areaEditor, setAreaEditor] = useState<AreaEditor>();
  const [areaPanelOpen, setAreaPanelOpen] = useState(false);
  const [nvrEditor, setNvrEditor] = useState<NvrEditor>();

  const loadSources = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: '50',
    });
    if (search.trim()) params.set('q', search.trim());
    if (areaId) params.set('areaId', areaId);
    if (onlineStatus) params.set('onlineStatus', onlineStatus);
    if (enabled) params.set('enabled', enabled);
    if (discoveryStatus) params.set('discoveryStatus', discoveryStatus);
    const result = await api<PagedResult<AdminVideoSource>>(
      `/admin/video-catalog/sources?${params.toString()}`,
    );
    setSources(result);
  }, [areaId, discoveryStatus, enabled, onlineStatus, page, search]);

  const loadMetadata = useCallback(async () => {
    const [areaRows, nvrRows, syncRows] = await Promise.all([
      api<AdminArea[]>('/admin/video-catalog/areas'),
      api<AdminNvrDevice[]>('/admin/video-catalog/nvr-devices'),
      api<VideoCatalogSyncLog[]>('/admin/video-catalog/sync-logs?limit=30'),
    ]);
    setAreas(areaRows);
    setNvrs(nvrRows);
    setLogs(syncRows);
  }, []);

  const refreshAll = useCallback(async () => {
    setError('');
    await Promise.all([loadSources(), loadMetadata()]);
  }, [loadMetadata, loadSources]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void Promise.all([loadSources(), loadMetadata()])
      .catch((reason) => {
        if (active) setError(readableError(reason, t('common.operationFailed')));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [loadMetadata, loadSources]);

  const summary = useMemo(() => {
    const online = sources.items.filter((source) => source.onlineStatus === 'ONLINE').length;
    const disabled = sources.items.filter((source) => !source.isEnabled).length;
    const pending = sources.items.filter((source) => source.discoveryStatus === 'DISCOVERED').length;
    return { online, disabled, pending };
  }, [sources.items]);

  async function runAction(action: () => Promise<string | void>, success: string) {
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const message = await action();
      await refreshAll();
      setNotice(message ?? success);
    } catch (reason) {
      setError(readableError(reason, t('common.operationFailed')));
    } finally {
      setWorking(false);
    }
  }

  async function synchronize() {
    await runAction(async () => {
      const result = await api<VideoCatalogSyncResult>('/admin/video-catalog/sync', {
        method: 'POST',
        body: JSON.stringify({ allowMissingSources: false }),
      });
      return t('streams.syncSummary', {
        added: result.addedCount,
        updated: result.updatedCount,
        missing: result.missingCount,
      });
    }, t('streams.syncSuccess'));
  }

  function openSourceEditor(source: AdminVideoSource) {
    setSourceEditor({
      id: source.id,
      displayName: source.displayName,
      areaId: source.areaId ? String(source.areaId) : '',
      isEnabled: source.isEnabled,
      sortOrder: source.sortOrder,
      notes: source.notes ?? '',
      updatedAt: source.updatedAt,
    });
  }

  async function saveSource(event: FormEvent) {
    event.preventDefault();
    const editor = sourceEditor;
    if (!editor) return;
    await runAction(async () => {
      await api<AdminVideoSource>(`/admin/video-catalog/sources/${editor.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          displayName: editor.displayName,
          areaId: editor.areaId ? Number(editor.areaId) : null,
          isEnabled: editor.isEnabled,
          sortOrder: Number(editor.sortOrder),
          notes: editor.notes || null,
          expectedUpdatedAt: editor.updatedAt,
        }),
      });
      setSourceEditor(undefined);
    }, t('streams.sourceSaved'));
  }

  async function changeDiscovery(source: AdminVideoSource, action: 'import' | 'ignore') {
    if (action === 'ignore' && !window.confirm(t('streams.confirmIgnore', { name: source.displayName }))) return;
    await runAction(async () => {
      await api<AdminVideoSource>(`/admin/video-catalog/sources/${source.id}/${action}`, {
        method: 'POST',
        body: JSON.stringify({
          expectedUpdatedAt: source.updatedAt,
          enableAfterImport: action === 'import',
        }),
      });
    }, action === 'import' ? t('streams.sourceImported') : t('streams.sourceIgnored'));
  }

  async function saveNvr(event: FormEvent) {
    event.preventDefault();
    const editor = nvrEditor;
    if (!editor) return;

    await runAction(async () => {
      await api<void>(`/admin/video-catalog/nvr-devices/${editor.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          displayName: editor.displayName,
          expectedUpdatedAt: editor.updatedAt,
        }),
      });
      setNvrEditor(undefined);
    }, t('streams.nvrNameSaved'));
  }

  function openNewArea() {
    setAreaEditor({ name: '', sortOrder: areas.length * 10, enabled: true });
  }

  function openAreaEditor(area: AdminArea) {
    setAreaEditor({
      id: area.id,
      name: area.name,
      sortOrder: area.sortOrder,
      enabled: area.enabled,
      updatedAt: area.updatedAt,
    });
  }

  async function saveArea(event: FormEvent) {
    event.preventDefault();
    const editor = areaEditor;
    if (!editor) return;
    await runAction(async () => {
      let savedArea: AdminArea;
      if (editor.id) {
        savedArea = await api<AdminArea>(`/admin/video-catalog/areas/${editor.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: editor.name,
            sortOrder: Number(editor.sortOrder),
            enabled: editor.enabled,
            expectedUpdatedAt: editor.updatedAt,
          }),
        });
      } else {
        savedArea = await api<AdminArea>('/admin/video-catalog/areas', {
          method: 'POST',
          body: JSON.stringify({
            name: editor.name,
            sortOrder: Number(editor.sortOrder),
            enabled: editor.enabled,
          }),
        });
        setSourceEditor((current) => current
          ? { ...current, areaId: String(savedArea.id) }
          : current);
      }
      setAreaEditor(undefined);
      return editor.id
        ? t('streams.areaUpdated', { name: savedArea.name })
        : sourceEditor
          ? t('streams.areaCreatedSelected', { name: savedArea.name })
          : t('streams.areaCreated', { name: savedArea.name });
    }, editor.id
      ? t('streams.areaUpdated', { name: editor.name })
      : t('streams.areaCreated', { name: editor.name }));
  }

  async function deleteArea(area: AdminArea) {
    if (!window.confirm(t('streams.confirmDeleteArea', { name: area.name }))) return;
    await runAction(async () => {
      await api<void>(
        `/admin/video-catalog/areas/${area.id}?expectedUpdatedAt=${encodeURIComponent(area.updatedAt)}`,
        { method: 'DELETE' },
      );
    }, t('streams.areaDeleted'));
  }

  const totalPages = Math.max(1, Math.ceil(sources.totalCount / sources.pageSize));

  return (
    <section className="page-section catalog-page">
      <header className="page-header catalog-header">
        <div>
          <p className="eyebrow">{t('streams.eyebrow')}</p>
          <h2>{t('streams.title')}</h2>
          <p className="muted catalog-subtitle">{t('streams.subtitle')}</p>
        </div>
        <div className="catalog-header-actions">
          <button className="area-management-button" onClick={() => setAreaPanelOpen(true)}>{t('streams.customAreas')}</button>
          <button disabled={working} onClick={() => void refreshAll()}>{t('streams.refreshStatus')}</button>
          <button className="primary-button" disabled={working} onClick={() => void synchronize()}>
            {working ? t('common.processing') : t('streams.syncNow')}
          </button>
        </div>
      </header>

      {error && <div className="notice error-notice">{error}</div>}
      {notice && <div className="notice success-notice">{notice}</div>}

      <div className="catalog-summary-grid">
        <article><span>{t('streams.sources')}</span><strong>{sources.totalCount}</strong><small>{t('streams.currentFilter')}</small></article>
        <article><span>{t('common.online')}</span><strong>{summary.online}</strong><small>{t('streams.pageLimit')}</small></article>
        <article><span>{t('streams.disabled')}</span><strong>{summary.disabled}</strong><small>{t('streams.pageStats')}</small></article>
        <article><span>{t('streams.discovered')}</span><strong>{summary.pending}</strong><small>{t('streams.waitImport')}</small></article>
        <article><span>NVR</span><strong>{nvrs.length}</strong><small>{t('streams.onlineNvrs', { count: nvrs.filter((nvr) => nvr.onlineStatus === 'ONLINE').length })}</small></article>
      </div>

      <div className="catalog-tabs">
        <button className={tab === 'sources' ? 'active' : ''} onClick={() => setTab('sources')}>{t('streams.sources')}</button>
        <button className={tab === 'nvrs' ? 'active' : ''} onClick={() => setTab('nvrs')}>{t('streams.nvrs')}</button>
        <button className={tab === 'sync' ? 'active' : ''} onClick={() => setTab('sync')}>{t('streams.syncLogs')}</button>
      </div>

      {tab === 'sources' && (
        <>
          <div className="catalog-filters">
            <input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder={t('streams.searchPlaceholder')} />
            <select value={areaId} onChange={(event) => { setAreaId(event.target.value); setPage(1); }}>
              <option value="">{t('streams.allAreas')}</option>
              {areas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}
            </select>
            <select value={onlineStatus} onChange={(event) => { setOnlineStatus(event.target.value); setPage(1); }}>
              <option value="">{t('streams.allOnline')}</option>
              <option value="ONLINE">{t('common.online')}</option>
              <option value="OFFLINE">{t('common.offline')}</option>
              <option value="UNKNOWN">{t('common.unknown')}</option>
            </select>
            <select value={enabled} onChange={(event) => { setEnabled(event.target.value); setPage(1); }}>
              <option value="">{t('streams.allEnabled')}</option>
              <option value="true">{t('common.enabled')}</option>
              <option value="false">{t('common.disabled')}</option>
            </select>
            <select value={discoveryStatus} onChange={(event) => { setDiscoveryStatus(event.target.value); setPage(1); }}>
              <option value="">{t('streams.allDiscovery')}</option>
              <option value="IMPORTED">{t('streams.imported')}</option>
              <option value="DISCOVERED">{t('streams.discovered')}</option>
              <option value="IGNORED">{t('streams.ignored')}</option>
            </select>
          </div>

          <div className="catalog-table-wrap">
            <table className="catalog-table">
              <thead><tr><th>{t('streams.source')}</th><th>{t('streams.areaNvr')}</th><th>{t('streams.origin')}</th><th>{t('common.online')}</th><th>{t('streams.catalogStatus')}</th><th>{t('streams.lastSync')}</th><th>{t('streams.actions')}</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="catalog-empty">{t('streams.reading')}</td></tr>}
                {!loading && sources.items.length === 0 && <tr><td colSpan={7} className="catalog-empty">{t('streams.noResults')}</td></tr>}
                {sources.items.map((source) => (
                  <tr key={source.id}>
                    <td>
                      <strong>{source.displayName}</strong>
                      <span>{source.upstreamName && source.upstreamName !== source.displayName ? source.upstreamName : source.channelId ?? `#${source.id}`}</span>
                    </td>
                    <td><strong>{source.areaName ?? t('streams.unassigned')}</strong><span>{source.nvrDisplayName ?? t('streams.manualSource')}</span></td>
                    <td><span className="source-type-badge">{source.sourceType}</span><small>{source.mainId ?? '—'}</small></td>
                    <td><span className={`catalog-status ${source.onlineStatus === 'ONLINE' ? 'online' : 'offline'}`}>{onlineLabel(source.onlineStatus, t)}</span><small>{source.isActivated ? t('streams.activated') : t('streams.notActivated')}</small></td>
                    <td><strong>{discoveryLabel(source.discoveryStatus, t)}</strong><span>{source.isEnabled ? t('streams.oapmEnabled') : t('streams.oapmDisabled')}</span></td>
                    <td>{formatDate(source.lastSyncAt)}</td>
                    <td className="catalog-actions">
                      <button onClick={() => openSourceEditor(source)}>{t('common.edit')}</button>
                      {source.discoveryStatus !== 'IMPORTED' && <button onClick={() => void changeDiscovery(source, 'import')}>{t('streams.import')}</button>}
                      {source.discoveryStatus !== 'IGNORED' && <button onClick={() => void changeDiscovery(source, 'ignore')}>{t('streams.ignore')}</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="catalog-pagination">
            <span>{t('streams.pageSummary', { count: sources.totalCount, page: sources.page, pages: totalPages })}</span>
            <div><button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>{t('streams.previousPage')}</button><button disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>{t('streams.nextPage')}</button></div>
          </div>
        </>
      )}

      {tab === 'nvrs' && (
        <div className="catalog-card-list">
          {nvrs.map((nvr) => (
            <article key={nvr.id} className="catalog-nvr-card">
              <div><span className={`status-dot ${nvr.onlineStatus === 'ONLINE' ? 'online' : 'offline'}`} /><div><h3>{nvr.displayName}</h3><p>{nvr.deviceId}</p></div></div>
              <dl><div><dt>{t('streams.channels')}</dt><dd>{nvr.channelCount}</dd></div><div><dt>{t('streams.imported')}</dt><dd>{nvr.importedCount}</dd></div><div><dt>{t('streams.missing')}</dt><dd>{nvr.missingCount}</dd></div><div><dt>{t('streams.lastSync')}</dt><dd>{formatDate(nvr.lastSyncAt)}</dd></div></dl>
              <div className="catalog-nvr-actions"><button onClick={() => setNvrEditor({ id: nvr.id, deviceId: nvr.deviceId, displayName: nvr.displayName, updatedAt: nvr.updatedAt })}>{t('streams.editNvr')}</button></div>
              {nvr.lastErrorMessage && <p className="catalog-inline-error">{nvr.lastErrorMessage}</p>}
            </article>
          ))}
        </div>
      )}

      {tab === 'sync' && (
        <div className="catalog-table-wrap">
          <table className="catalog-table sync-table">
            <thead><tr><th>{t('streams.time')}</th><th>{t('streams.trigger')}</th><th>{t('streams.status')}</th><th>{t('streams.added')}</th><th>{t('streams.updated')}</th><th>{t('streams.missing')}</th><th>{t('streams.error')}</th></tr></thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}><td>{formatDate(log.startedAt)}</td><td>{log.triggerType}</td><td><span className={`catalog-status ${log.status === 'SUCCESS' ? 'online' : 'offline'}`}>{log.status}</span></td><td>{log.addedCount}</td><td>{log.updatedCount}</td><td>{log.missingCount}</td><td>{log.errorMessage ?? '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nvrEditor && (
        <div className="modal-backdrop" onMouseDown={() => setNvrEditor(undefined)}>
          <form className="catalog-modal" onSubmit={(event) => void saveNvr(event)} onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div><p className="eyebrow">NVR SETTINGS</p><h3>{t('streams.editNvr')}</h3><span className="modal-subtitle">{nvrEditor.deviceId}</span></div>
              <button type="button" onClick={() => setNvrEditor(undefined)}>{t('common.close')}</button>
            </header>
            <label>{t('streams.nvrDisplayName')}<input required maxLength={255} value={nvrEditor.displayName} onChange={(event) => setNvrEditor({ ...nvrEditor, displayName: event.target.value })} /></label>
            <footer><button type="button" onClick={() => setNvrEditor(undefined)}>{t('common.cancel')}</button><button className="primary-button" disabled={working}>{t('common.save')}</button></footer>
          </form>
        </div>
      )}

      {sourceEditor && (
        <div className="modal-backdrop" onMouseDown={() => setSourceEditor(undefined)}>
          <form className="catalog-modal" onSubmit={(event) => void saveSource(event)} onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">SOURCE SETTINGS</p><h3>{t('streams.editSource')}</h3></div><button type="button" onClick={() => setSourceEditor(undefined)}>{t('common.close')}</button></header>
            <label>{t('users.displayName')}<input required value={sourceEditor.displayName} onChange={(event) => setSourceEditor({ ...sourceEditor, displayName: event.target.value })} /></label>
            <label>
              {t('streams.area')}
              <span className="area-select-row">
                <select value={sourceEditor.areaId} onChange={(event) => setSourceEditor({ ...sourceEditor, areaId: event.target.value })}>
                  <option value="">{t('streams.noArea')}</option>
                  {areas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}
                </select>
                <button
                  type="button"
                  className="area-inline-manage"
                  onClick={() => { setAreaPanelOpen(true); setAreaEditor(undefined); }}
                >
                  {t('streams.manageAreas')}
                </button>
              </span>
            </label>
            <label>{t('streams.sortOrder')}<input type="number" value={sourceEditor.sortOrder} onChange={(event) => setSourceEditor({ ...sourceEditor, sortOrder: Number(event.target.value) })} /></label>
            <label className="checkbox-label"><input type="checkbox" checked={sourceEditor.isEnabled} onChange={(event) => setSourceEditor({ ...sourceEditor, isEnabled: event.target.checked })} />{t('streams.enableOapm')}</label>
            <label>{t('streams.notes')}<textarea value={sourceEditor.notes} onChange={(event) => setSourceEditor({ ...sourceEditor, notes: event.target.value })} /></label>
            <footer><button type="button" onClick={() => setSourceEditor(undefined)}>{t('common.cancel')}</button><button className="primary-button" disabled={working}>{t('common.save')}</button></footer>
          </form>
        </div>
      )}

      {areaPanelOpen && (
        <div className="modal-backdrop" onMouseDown={() => { setAreaPanelOpen(false); setAreaEditor(undefined); }}>
          <div className="catalog-modal area-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">AREA DIRECTORY</p><h3>{t('streams.areaDirectory')}</h3><span className="modal-subtitle">{t('streams.areaHelp')}</span></div><button onClick={() => { setAreaPanelOpen(false); setAreaEditor(undefined); }}>{t('common.close')}</button></header>
            <div className="area-list">
              {areas.map((area) => <div key={area.id}><div><strong>{area.name}</strong><span>{t('streams.areaCount', { count: area.sourceCount, order: area.sortOrder, status: area.enabled ? t('common.enabled') : t('common.disabled') })}</span></div><div><button onClick={() => openAreaEditor(area)}>{t('common.edit')}</button><button disabled={area.sourceCount > 0} onClick={() => void deleteArea(area)}>{t('common.delete')}</button></div></div>)}
            </div>
            {!areaEditor && <button className="primary-button" onClick={openNewArea}>{t('streams.newArea')}</button>}
            {areaEditor && <form className="area-editor" onSubmit={(event) => void saveArea(event)}><label>{t('streams.name')}<input required value={areaEditor.name} onChange={(event) => setAreaEditor({ ...areaEditor, name: event.target.value })} /></label><label>{t('streams.sortOrder')}<input type="number" value={areaEditor.sortOrder} onChange={(event) => setAreaEditor({ ...areaEditor, sortOrder: Number(event.target.value) })} /></label><label className="checkbox-label"><input type="checkbox" checked={areaEditor.enabled} onChange={(event) => setAreaEditor({ ...areaEditor, enabled: event.target.checked })} />{t('streams.enableArea')}</label><footer><button type="button" onClick={() => setAreaEditor(undefined)}>{t('common.cancel')}</button><button className="primary-button" disabled={working}>{t('streams.saveArea')}</button></footer></form>}
          </div>
        </div>
      )}
    </section>
  );
}
